import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  XCircle, 
  ArrowLeft,
  RefreshCw,
  Shield,
  HelpCircle
} from 'lucide-react';

const UpgradeCancelled = () => {
  const navigate = useNavigate();

  const retryUpgrade = () => {
    navigate('/basic-portal');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-green-600 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">
                Legacy Property Association
              </h1>
            </div>
            <Badge className="bg-green-100 text-green-800">
              Basic Member
            </Badge>
          </div>
        </div>
      </div>

      {/* Cancelled Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="text-center">
          <CardContent className="p-8">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-slate-500" />
            </div>
            
            <h2 className="text-2xl font-semibold text-slate-800 mb-4">
              Upgrade Cancelled
            </h2>
            
            <p className="text-slate-600 mb-6 leading-relaxed">
              No worries! Your upgrade process was cancelled and no charges were made. 
              Your Basic membership remains active with all its benefits.
            </p>

            {/* Current Benefits Reminder */}
            <Card className="bg-green-50 border-green-200 mb-6">
              <CardHeader>
                <CardTitle className="text-lg text-green-800">Your Basic Benefits Continue</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-left space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-sm text-green-700">Legal documents & forms access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-sm text-green-700">Educational resource library</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-sm text-green-700">⭐ FREE GC & Contractor sign-up</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-sm text-green-700">Member community access</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span className="text-sm text-green-700">Monthly educational webinars</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* What You Would Have Gained */}
            <Card className="bg-amber-50 border-amber-200 mb-8">
              <CardHeader>
                <CardTitle className="text-lg text-amber-800">What You're Missing Out On</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-left space-y-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-amber-600 rounded-full"></div>
                    <span className="text-sm text-amber-700">Property management portal</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-amber-600 rounded-full"></div>
                    <span className="text-sm text-amber-700">🔧 Access to General Contractor & Skilled Laborers Network</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-amber-600 rounded-full"></div>
                    <span className="text-sm text-amber-700">Tenant payment coordination</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-amber-600 rounded-full"></div>
                    <span className="text-sm text-amber-700">Maintenance request system</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="text-sm text-blue-700">📊 Reporting Tools (Premium)</span>
                  </div>
                </div>
                <p className="text-xs text-amber-600 mt-3">
                  💡 Standard: $50/month • Premium: $100/month with Reporting Tools
                </p>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-4">
              <Button 
                onClick={retryUpgrade}
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Upgrade Again
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => navigate('/basic-portal')}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return to Basic Portal
              </Button>
            </div>

            {/* Help Section */}
            <div className="mt-8 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <HelpCircle className="w-5 h-5 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Need Help?</span>
              </div>
              <p className="text-xs text-slate-600">
                If you experienced any issues during the upgrade process, please contact our support team at 
                <span className="font-medium"> support@legacypropertyassociation.com</span>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UpgradeCancelled;